import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { useProjects } from '../../hooks/useProjects';
import {
  usePreliquidacion,
  useCreatePreliquidacion,
  useAddGastoAdicional,
  useUpdateGastoAdicional,
  useDeleteGastoAdicional,
  useAddReingreso,
  useUpdateReingreso,
  useDeleteReingreso,
  useCompletePreliquidacion,
  useLiquidarProyecto,
  type CostoItemDTO,
  type ManoObraItemDTO,
  type GastoAdicionalDTO,
  type ReingresosDTO,
} from '../../hooks/usePreliquidacion';
import { useAvanceFinanciero, useAvanceReporte } from '../../hooks/useAvance';
import { useAuthStore } from '../../stores/auth-store';
import { toast } from '../../components/ui/Toast';
import { AdvanceBar } from '../../components/shared/AdvanceBar';
import { DollarSign, Plus, Check, ShieldCheck, ClipboardList, ChevronDown, Pencil, Trash2 } from 'lucide-react';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount);
}

export function LiquidacionPage() {
  const [searchParams] = useSearchParams();
  const [selectedProjectId, setSelectedProjectId] = useState(searchParams.get('projectId') ?? '');
  const [showGastoModal, setShowGastoModal] = useState(false);
  const [gastoDescripcion, setGastoDescripcion] = useState('');
  const [gastoMonto, setGastoMonto] = useState('');
  const [editingGasto, setEditingGasto] = useState<GastoAdicionalDTO | null>(null);
  const [editDescripcion, setEditDescripcion] = useState('');
  const [editMonto, setEditMonto] = useState('');
  const [deletingGastoId, setDeletingGastoId] = useState<string | null>(null);
  const [showReingresosModal, setShowReingresosModal] = useState(false);
  const [reingresosDescripcion, setReingresosDescripcion] = useState('');
  const [reingresosMontoStr, setReingresosMontoStr] = useState('');
  const [editingReingreso, setEditingReingreso] = useState<ReingresosDTO | null>(null);
  const [editReingresoDescripcion, setEditReingresoDescripcion] = useState('');
  const [editReingresoMonto, setEditReingresoMonto] = useState('');
  const [deletingReingresosId, setDeletingReingresosId] = useState<string | null>(null);
  const [showLiquidarModal, setShowLiquidarModal] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const { user } = useAuthStore();
  const isAdmin = user?.role === 'administrativo';
  const isGerente = user?.role === 'gerente';
  const isOperaciones = user?.role === 'operaciones';
  const isCompras = user?.role === 'compras';
  const canCreatePreliq = isAdmin || isGerente || isOperaciones;
  const canAddGastos = isAdmin || isCompras || isGerente || isOperaciones;

  const { data: projectsData, isLoading: loadingProjects } = useProjects({ page: 1, limit: 500 });
  const { data: financiero } = useAvanceFinanciero(selectedProjectId);
  const { data: reporte } = useAvanceReporte(selectedProjectId);
  const { data: preliq, isLoading: loadingPreliq } = usePreliquidacion(selectedProjectId);
  const createPreliq = useCreatePreliquidacion(selectedProjectId);
  const addGasto = useAddGastoAdicional(preliq?.id ?? '', selectedProjectId);
  const updateGasto = useUpdateGastoAdicional(selectedProjectId);
  const deleteGasto = useDeleteGastoAdicional(selectedProjectId);
  const addReingreso = useAddReingreso(preliq?.id ?? '', selectedProjectId);
  const updateReingreso = useUpdateReingreso(selectedProjectId);
  const deleteReingreso = useDeleteReingreso(selectedProjectId);
  const completePreliq = useCompletePreliquidacion(preliq?.id ?? '', selectedProjectId);
  const liquidarProyecto = useLiquidarProyecto(preliq?.id ?? '', selectedProjectId);

  const projects = projectsData?.data ?? [];
  const projectOptions = projects.map((p) => ({
    value: p.id,
    label: `${p.codigo} — ${p.nombre}`,
  }));
  const selectedProject = projects.find((p) => p.id === selectedProjectId);
  const projectoYaLiquidado = selectedProject?.estado === 'finalizado' || selectedProject?.estado === 'liquidado';

  const handleCreatePreliq = async () => {
    try {
      await createPreliq.mutateAsync(undefined);
      toast('success', 'Pre-liquidación creada');
    } catch (err: unknown) {
      // 409 = already exists (e.g. created on a previous attempt that partially failed)
      // onSettled already invalidated the cache so the existing preliq will load automatically
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 409) {
        toast('error', 'Error al crear pre-liquidación');
      }
    }
  };

  const handleAddGasto = async () => {
    if (!gastoDescripcion || !gastoMonto) return;
    try {
      await addGasto.mutateAsync({
        categoria: 'otros',
        descripcion: gastoDescripcion,
        monto: Number(gastoMonto),
      });
      toast('success', 'Gasto adicional agregado');
      setShowGastoModal(false);
      setGastoDescripcion('');
      setGastoMonto('');
    } catch {
      toast('error', 'Error al agregar gasto');
    }
  };

  const openEditGasto = (g: GastoAdicionalDTO) => {
    setEditingGasto(g);
    setEditDescripcion(g.descripcion);
    setEditMonto(String(g.monto));
  };

  const handleUpdateGasto = async () => {
    if (!editingGasto || !editDescripcion || !editMonto) return;
    try {
      await updateGasto.mutateAsync({
        gastoId: editingGasto.id,
        descripcion: editDescripcion,
        monto: Number(editMonto),
      });
      toast('success', 'Gasto actualizado');
      setEditingGasto(null);
    } catch {
      toast('error', 'Error al actualizar gasto');
    }
  };

  const handleDeleteGasto = async () => {
    if (!deletingGastoId) return;
    try {
      await deleteGasto.mutateAsync(deletingGastoId);
      toast('success', 'Gasto eliminado');
      setDeletingGastoId(null);
    } catch {
      toast('error', 'Error al eliminar gasto');
    }
  };

  const handleAddReingreso = async () => {
    if (!reingresosDescripcion || !reingresosMontoStr) return;
    try {
      await addReingreso.mutateAsync({
        categoria: 'otros',
        descripcion: reingresosDescripcion,
        monto: Number(reingresosMontoStr),
      });
      toast('success', 'Reingreso agregado');
      setShowReingresosModal(false);
      setReingresosDescripcion('');
      setReingresosMontoStr('');
    } catch {
      toast('error', 'Error al agregar reingreso');
    }
  };

  const openEditReingreso = (r: ReingresosDTO) => {
    setEditingReingreso(r);
    setEditReingresoDescripcion(r.descripcion);
    setEditReingresoMonto(String(r.monto));
  };

  const handleUpdateReingreso = async () => {
    if (!editingReingreso || !editReingresoDescripcion || !editReingresoMonto) return;
    try {
      await updateReingreso.mutateAsync({
        reingresosId: editingReingreso.id,
        descripcion: editReingresoDescripcion,
        monto: Number(editReingresoMonto),
      });
      toast('success', 'Reingreso actualizado');
      setEditingReingreso(null);
    } catch {
      toast('error', 'Error al actualizar reingreso');
    }
  };

  const handleDeleteReingreso = async () => {
    if (!deletingReingresosId) return;
    try {
      await deleteReingreso.mutateAsync(deletingReingresosId);
      toast('success', 'Reingreso eliminado');
      setDeletingReingresosId(null);
    } catch {
      toast('error', 'Error al eliminar reingreso');
    }
  };

  const handleComplete = async () => {
    try {
      await completePreliq.mutateAsync();
      toast('success', 'Pre-liquidación completada');
    } catch {
      toast('error', 'Error al completar pre-liquidación');
    }
  };

  const resumen = preliq?.resumen;
  const montoEjecutado = reporte?.total_acumulado ?? 0;
  const montoEjecutadoSinIva = reporte?.total_sin_iva_acumulado ?? 0;
  const totalProforma = financiero?.total_proforma ?? 0;
  const pctEjecucion = totalProforma > 0 ? Math.round((montoEjecutado / totalProforma) * 10000) / 100 : 0;

  return (
    <div className="space-y-6 fade-up">
      <h1 className="text-2xl font-bold text-brand-dark">Liquidación</h1>

      {/* Project selector */}
      <Card>
        <div className="max-w-md">
          {loadingProjects ? (
            <Skeleton className="h-10 w-full" />
          ) : (
            <Select
              label="Proyecto"
              value={selectedProjectId}
              onChange={(e) => {
                setSelectedProjectId(e.target.value);
              }}
              options={[
                { value: '', label: 'Seleccionar proyecto...' },
                ...projectOptions,
              ]}
            />
          )}
        </div>
      </Card>

      {/* Financial overview cards + execution bar — visible as soon as a project is selected */}
      {selectedProjectId && financiero && totalProforma > 0 && (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatCard
              label="Total Proforma (c/IVA)"
              value={formatCurrency(totalProforma)}
              sub=""
            />
            <StatCard
              label="Monto Ejecutado (c/IVA)"
              value={formatCurrency(montoEjecutado)}
              sub={`${pctEjecucion}% ejecutado`}
              highlight={pctEjecucion >= 100}
            />
            <StatCard
              label="Monto Ejecutado (sin IVA)"
              value={formatCurrency(montoEjecutadoSinIva)}
              sub="base para utilidad real"
            />
          </div>

          <Card>
            <h3 className="mb-2 text-sm font-semibold text-brand-dark">Ejecución de Proforma</h3>
            <AdvanceBar
              consumed={montoEjecutado}
              total={totalProforma}
              mode="progress"
            />
          </Card>
        </>
      )}


      {selectedProjectId && loadingPreliq && (
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      )}

      {/* Empty state — no preliq yet: show prominent CTA */}
      {selectedProjectId && !loadingPreliq && !preliq?.id && (
        <Card>
          <div className="flex flex-col items-center py-10 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-dark/10">
              <ClipboardList size={32} className="text-brand-dark" />
            </div>
            <h2 className="mb-2 text-lg font-semibold text-brand-dark">
              Iniciar Pre-Liquidación
            </h2>
            <p className="mb-6 max-w-sm text-sm text-gray-500">
              La pre-liquidación consolida el resumen financiero del proyecto: pagos a maestros,
              materiales despachados y gastos adicionales. Es el primer paso para el cierre del proyecto.
            </p>
            {canCreatePreliq ? (
              <Button
                variant="primary"
                size="lg"
                onClick={handleCreatePreliq}
                isLoading={createPreliq.isPending}
              >
                <Plus size={18} className="mr-2" />
                Crear Pre-Liquidación
              </Button>
            ) : (
              <p className="text-sm text-gray-400">
                Solo administrativo, gerente u operaciones pueden iniciar la pre-liquidación.
              </p>
            )}
          </div>
        </Card>
      )}

      {preliq?.id && resumen && (
        <>
          {/* Financial Summary */}
          <Card>
            <h2 className="mb-4 text-lg font-semibold text-brand-dark">
              <DollarSign size={20} className="mr-2 inline" />
              Resumen Financiero
              {preliq.estado === 'completada' && (
                <span className="ml-2 rounded bg-green-100 px-2 py-0.5 text-xs text-green-700">
                  Completada
                </span>
              )}
            </h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SummaryCard label="Ingreso Pactado (sin IVA)" value={resumen.total_proforma} />
              <SummaryCard label="Total Pagos" value={resumen.total_anticipos} />
            </div>

            {/* Cost breakdown */}
            <div className="mt-4 space-y-2">
              <AccordionManoObra
                total={resumen.costo_mano_obra}
                items={resumen.items_mano_obra}
                isOpen={expandedGroups.has('mano_obra')}
                onToggle={() => toggleGroup('mano_obra')}
              />
              <AccordionCostoGroup
                label="Materiales"
                headerClass="border-teal-100 bg-teal-50 text-teal-800"
                total={resumen.costo_materiales}
                items={resumen.items_materiales}
                isOpen={expandedGroups.has('materiales')}
                onToggle={() => toggleGroup('materiales')}
              />
              <p className="pt-2 text-xs font-semibold uppercase text-gray-400">Costos Adicionales</p>
              <div className="rounded-lg border border-gray-100 divide-y divide-gray-50">
                <div className="flex justify-between px-4 py-2 text-sm">
                  <span className="text-gray-600">Gastos adicionales</span>
                  <span className="font-medium">{formatCurrency(resumen.total_gastos_adicionales)}</span>
                </div>
                {resumen.total_reingresos > 0 && (
                  <div className="flex justify-between px-4 py-2 text-sm">
                    <span className="text-green-700">Reingresos</span>
                    <span className="font-medium text-green-700">+{formatCurrency(resumen.total_reingresos)}</span>
                  </div>
                )}
              </div>
              <AccordionCostoGroup
                label="Materiales Adicionales"
                headerClass="border-orange-100 bg-orange-50 text-orange-800"
                total={resumen.costo_materiales_adicionales}
                items={resumen.items_materiales_adicionales}
                isOpen={expandedGroups.has('mat_adicionales')}
                onToggle={() => toggleGroup('mat_adicionales')}
              />
              <AccordionCostoGroup
                label="Materiales Excedente"
                headerClass="border-amber-100 bg-amber-50 text-amber-800"
                total={resumen.costo_excedentes}
                items={resumen.items_excedentes}
                isOpen={expandedGroups.has('excedentes')}
                onToggle={() => toggleGroup('excedentes')}
              />
              <AccordionCostoGroup
                label="Insumos Adicionales"
                headerClass="border-purple-100 bg-purple-50 text-purple-800"
                total={resumen.costo_insumos}
                items={resumen.items_insumos}
                isOpen={expandedGroups.has('insumos')}
                onToggle={() => toggleGroup('insumos')}
              />
            </div>

          </Card>

          {/* Gastos Adicionales */}
          <Card>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-brand-dark">
                Gastos Adicionales
              </h2>
              {canAddGastos && !projectoYaLiquidado && (
                <Button variant="secondary" size="sm" onClick={() => setShowGastoModal(true)}>
                  <Plus size={14} className="mr-1" />
                  Agregar
                </Button>
              )}
            </div>

            {preliq.gastos_adicionales.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">
                No hay gastos adicionales registrados
              </p>
            ) : (
              <div className="space-y-2">
                {preliq.gastos_adicionales.map((g) => (
                  <div
                    key={g.id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="rounded bg-gray-100 px-2 py-0.5 text-xs capitalize text-gray-600">
                        {g.categoria.replace('_', ' ')}
                      </span>
                      <span className="ml-2 text-sm">{g.descripcion}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <span className="font-medium">{formatCurrency(g.monto)}</span>
                      {canAddGastos && !projectoYaLiquidado && (
                        <>
                          <button
                            type="button"
                            onClick={() => openEditGasto(g)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Editar"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletingGastoId(g.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Reingresos */}
          <Card>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-brand-dark">
                Reingresos
              </h2>
              {canAddGastos && !projectoYaLiquidado && (
                <Button variant="secondary" size="sm" onClick={() => setShowReingresosModal(true)}>
                  <Plus size={14} className="mr-1" />
                  Agregar
                </Button>
              )}
            </div>

            <p className="mb-3 text-xs text-gray-400">
              Los reingresos son ingresos adicionales que suman directamente a la utilidad del proyecto
              (devoluciones, cobros adicionales u otros).
            </p>

            {(preliq.reingresos ?? []).length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-400">
                No hay reingresos registrados
              </p>
            ) : (
              <div className="space-y-2">
                {(preliq.reingresos ?? []).map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between rounded-lg border border-green-100 bg-green-50/30 p-3"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="rounded bg-green-100 px-2 py-0.5 text-xs capitalize text-green-700">
                        {r.categoria.replace('_', ' ')}
                      </span>
                      <span className="ml-2 text-sm">{r.descripcion}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <span className="font-medium text-green-700">+{formatCurrency(r.monto)}</span>
                      {canAddGastos && !projectoYaLiquidado && (
                        <>
                          <button
                            type="button"
                            onClick={() => openEditReingreso(r)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title="Editar"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletingReingresosId(r.id)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Costo Total</p>
                  <p className="text-lg font-bold text-brand-dark">{formatCurrency(resumen.costo_total)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Utilidad Real</p>
                  <p className={`text-lg font-bold ${resumen.margen >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(resumen.margen)} ({resumen.margen_porcentaje}%)
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Actions */}
          {canCreatePreliq && preliq.estado === 'borrador' && (
            <div className="flex gap-3">
              <Button
                variant="primary"
                onClick={handleComplete}
                isLoading={completePreliq.isPending}
              >
                <Check size={16} className="mr-2" />
                Completar Pre-Liquidación
              </Button>
            </div>
          )}

          {preliq.estado === 'completada' && canCreatePreliq && !projectoYaLiquidado && (
            <div className="flex gap-3">
              <Button variant="primary" onClick={() => setShowLiquidarModal(true)}>
                <ShieldCheck size={16} className="mr-2" />
                Liquidar Proyecto
              </Button>
            </div>
          )}

          {preliq.estado === 'completada' && !canCreatePreliq && !projectoYaLiquidado && (
            <p className="text-sm text-green-600 font-medium">
              ✓ Pre-liquidación completada — pendiente de liquidación por gerente o administrador
            </p>
          )}

          {projectoYaLiquidado && (
            <p className="text-sm text-gray-500 font-medium">
              ✓ Proyecto liquidado y cerrado
            </p>
          )}
        </>
      )}

      {/* Liquidar Modal */}
      <Modal
        isOpen={showLiquidarModal}
        onClose={() => setShowLiquidarModal(false)}
        title="Confirmar Liquidación"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowLiquidarModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  await liquidarProyecto.mutateAsync();
                  toast('success', 'Proyecto liquidado exitosamente');
                  setShowLiquidarModal(false);
                } catch {
                  toast('error', 'Error al liquidar proyecto');
                }
              }}
              isLoading={liquidarProyecto.isPending}
            >
              Confirmar Liquidación
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          ¿Confirmar la liquidación del proyecto? El estado pasará a{' '}
          <strong>Liquidado</strong> y todos los datos serán de solo lectura.
        </p>
      </Modal>

      {/* Edit Gasto Modal */}
      <Modal
        isOpen={!!editingGasto}
        onClose={() => setEditingGasto(null)}
        title="Editar Gasto Adicional"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setEditingGasto(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateGasto}
              isLoading={updateGasto.isPending}
              disabled={!editDescripcion || !editMonto}
            >
              Guardar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Descripción"
            value={editDescripcion}
            onChange={(e) => setEditDescripcion(e.target.value)}
            placeholder="Descripción del gasto"
          />
          <Input
            label="Monto (USD)"
            type="number"
            value={editMonto}
            onChange={(e) => setEditMonto(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </Modal>

      {/* Delete Gasto Confirm Modal */}
      <Modal
        isOpen={!!deletingGastoId}
        onClose={() => setDeletingGastoId(null)}
        title="Eliminar Gasto Adicional"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeletingGastoId(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteGasto}
              isLoading={deleteGasto.isPending}
            >
              Eliminar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          ¿Confirmar la eliminación de este gasto adicional? Esta acción no se puede deshacer.
        </p>
      </Modal>

      {/* Add Reingreso Modal */}
      <Modal
        isOpen={showReingresosModal}
        onClose={() => setShowReingresosModal(false)}
        title="Agregar Reingreso"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowReingresosModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAddReingreso}
              isLoading={addReingreso.isPending}
              disabled={!reingresosDescripcion || !reingresosMontoStr}
            >
              Agregar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Descripción"
            value={reingresosDescripcion}
            onChange={(e) => setReingresosDescripcion(e.target.value)}
            placeholder="Descripción del reingreso"
          />
          <Input
            label="Monto (USD)"
            type="number"
            value={reingresosMontoStr}
            onChange={(e) => setReingresosMontoStr(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </Modal>

      {/* Edit Reingreso Modal */}
      <Modal
        isOpen={!!editingReingreso}
        onClose={() => setEditingReingreso(null)}
        title="Editar Reingreso"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setEditingReingreso(null)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleUpdateReingreso}
              isLoading={updateReingreso.isPending}
              disabled={!editReingresoDescripcion || !editReingresoMonto}
            >
              Guardar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Descripción"
            value={editReingresoDescripcion}
            onChange={(e) => setEditReingresoDescripcion(e.target.value)}
            placeholder="Descripción del reingreso"
          />
          <Input
            label="Monto (USD)"
            type="number"
            value={editReingresoMonto}
            onChange={(e) => setEditReingresoMonto(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </Modal>

      {/* Delete Reingreso Confirm Modal */}
      <Modal
        isOpen={!!deletingReingresosId}
        onClose={() => setDeletingReingresosId(null)}
        title="Eliminar Reingreso"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setDeletingReingresosId(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteReingreso}
              isLoading={deleteReingreso.isPending}
            >
              Eliminar
            </Button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          ¿Confirmar la eliminación de este reingreso? Esta acción no se puede deshacer.
        </p>
      </Modal>

      {/* Add Gasto Modal */}
      <Modal
        isOpen={showGastoModal}
        onClose={() => setShowGastoModal(false)}
        title="Agregar Gasto Adicional"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setShowGastoModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleAddGasto}
              isLoading={addGasto.isPending}
              disabled={!gastoDescripcion || !gastoMonto}
            >
              Agregar
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Descripción"
            value={gastoDescripcion}
            onChange={(e) => setGastoDescripcion(e.target.value)}
            placeholder="Descripción del gasto"
          />
          <Input
            label="Monto (USD)"
            type="number"
            value={gastoMonto}
            onChange={(e) => setGastoMonto(e.target.value)}
            placeholder="0.00"
          />
        </div>
      </Modal>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-100 p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-brand-dark">{formatCurrency(value)}</p>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-white'}`}>
      <p className="mb-1 text-xs text-gray-500">{label}</p>
      <p className={`text-xl font-bold ${highlight ? 'text-green-700' : 'text-brand-dark'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </div>
  );
}

// ── AccordionManoObra ──────────────────────────────────────────────

const CATEGORIA_LABEL: Record<string, string> = {
  gypsum: 'Gypsum',
  pintura: 'Pintura',
  varios: 'Servicios Varios',
  varios_maestro: 'Varios Maestro',
  multa: 'Multa',
  herramienta: 'Herramienta',
  anticipo_maestro: 'Anticipo Maestro',
  administrativo: 'Administrativo',
  otro: 'Otro',
};

function AccordionManoObra({
  total,
  items,
  isOpen,
  onToggle,
}: {
  total: number;
  items: ManoObraItemDTO[];
  isOpen: boolean;
  onToggle: () => void;
}) {
  const apuItems = items.filter((i) => i.tipo === 'apu');
  const adicionalItems = items.filter((i) => i.tipo === 'adicional');
  const svItems = items.filter((i) => i.tipo === 'servicio_varios');
  const descuentoItems = items.filter((i) => i.tipo === 'descuento');

  return (
    <div className="rounded-lg border border-blue-100 overflow-hidden bg-blue-50 text-blue-800">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-sm"
        onClick={onToggle}
      >
        <span className="font-medium">Mano de Obra</span>
        <div className="flex items-center gap-3">
          <span className="font-semibold">{formatCurrency(total)}</span>
          <span className="text-xs opacity-70">
            {items.length} ítem{items.length !== 1 ? 's' : ''}
          </span>
          <ChevronDown
            size={14}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-blue-200/60 divide-y divide-blue-200/40">
          {/* APUs Regulares */}
          {apuItems.length > 0 && (
            <div>
              <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase opacity-60 tracking-wide">
                APUs Regulares
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left opacity-60">
                      <th className="px-4 py-1.5 font-medium">APU</th>
                      <th className="px-4 py-1.5 font-medium">MDO</th>
                      <th className="px-4 py-1.5 font-medium text-right">Cant.</th>
                      <th className="px-4 py-1.5 font-medium text-right">P. Unit.</th>
                      <th className="px-4 py-1.5 font-medium text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apuItems.map((item, i) => (
                      <tr key={i} className="border-t border-blue-200/30">
                        <td className="px-4 py-1.5">{item.apu_nombre}</td>
                        <td className="px-4 py-1.5 text-blue-600/70">{item.mdo_nombre ?? '—'}</td>
                        <td className="px-4 py-1.5 text-right">{item.cantidad}</td>
                        <td className="px-4 py-1.5 text-right">{formatCurrency(item.precio_unitario ?? 0)}</td>
                        <td className="px-4 py-1.5 text-right font-medium">{formatCurrency(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Adicionales (con APU) */}
          {adicionalItems.length > 0 && (
            <div>
              <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase opacity-60 tracking-wide">
                Adicionales
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left opacity-60">
                      <th className="px-4 py-1.5 font-medium">APU</th>
                      <th className="px-4 py-1.5 font-medium">Descripción</th>
                      <th className="px-4 py-1.5 font-medium text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adicionalItems.map((item, i) => (
                      <tr key={i} className="border-t border-blue-200/30">
                        <td className="px-4 py-1.5">{item.apu_nombre}</td>
                        <td className="px-4 py-1.5 text-blue-600/70">{item.descripcion ?? '—'}</td>
                        <td className="px-4 py-1.5 text-right font-medium">{formatCurrency(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Servicios Varios */}
          {svItems.length > 0 && (
            <div>
              <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase opacity-60 tracking-wide">
                Servicios Varios
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left opacity-60">
                      <th className="px-4 py-1.5 font-medium">Descripción</th>
                      <th className="px-4 py-1.5 font-medium">Categoría</th>
                      <th className="px-4 py-1.5 font-medium text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {svItems.map((item, i) => (
                      <tr key={i} className="border-t border-blue-200/30">
                        <td className="px-4 py-1.5">{item.descripcion ?? '—'}</td>
                        <td className="px-4 py-1.5 text-blue-600/70">
                          {CATEGORIA_LABEL[item.categoria ?? ''] ?? item.categoria ?? '—'}
                        </td>
                        <td className="px-4 py-1.5 text-right font-medium">{formatCurrency(item.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Descuentos / Multas */}
          {descuentoItems.length > 0 && (
            <div>
              <p className="px-4 pt-2 pb-1 text-xs font-semibold uppercase opacity-60 tracking-wide">
                Descuentos / Multas
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left opacity-60">
                      <th className="px-4 py-1.5 font-medium">Tipo</th>
                      <th className="px-4 py-1.5 font-medium">Descripción</th>
                      <th className="px-4 py-1.5 font-medium text-right">Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {descuentoItems.map((item, i) => (
                      <tr key={i} className="border-t border-blue-200/30">
                        <td className="px-4 py-1.5 text-red-600">
                          {CATEGORIA_LABEL[item.categoria ?? ''] ?? item.categoria ?? '—'}
                        </td>
                        <td className="px-4 py-1.5 text-blue-600/70">{item.descripcion ?? '—'}</td>
                        <td className="px-4 py-1.5 text-right font-medium text-red-600">
                          −{formatCurrency(Math.abs(item.subtotal))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {items.length === 0 && (
            <p className="px-4 py-3 text-xs opacity-60">Sin registros</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── AccordionCostoGroup ─────────────────────────────────────────────

function AccordionCostoGroup({
  label,
  headerClass,
  total,
  items,
  isOpen,
  onToggle,
}: {
  label: string;
  headerClass: string;
  total: number;
  items: CostoItemDTO[];
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className={`rounded-lg border overflow-hidden ${headerClass}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 text-sm"
        onClick={onToggle}
      >
        <span className="font-medium">{label}</span>
        <div className="flex items-center gap-3">
          <span className="font-semibold">{formatCurrency(total)}</span>
          <span className="text-xs opacity-70">
            {items.length} ítem{items.length !== 1 ? 's' : ''}
          </span>
          <ChevronDown
            size={14}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {isOpen && items.length > 0 && (
        <div className="border-t border-black/10 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left opacity-60">
                <th className="px-4 py-2 font-medium">Nombre</th>
                <th className="px-4 py-2 font-medium text-right">Cant.</th>
                <th className="px-4 py-2 font-medium text-right">P. Unit.</th>
                <th className="px-4 py-2 font-medium text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className="border-t border-black/10">
                  <td className="px-4 py-1.5">{item.nombre}</td>
                  <td className="px-4 py-1.5 text-right">{item.cantidad}</td>
                  <td className="px-4 py-1.5 text-right">{formatCurrency(item.precio_unitario)}</td>
                  <td className="px-4 py-1.5 text-right font-medium">{formatCurrency(item.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

